<template>
  <div class="view-lesson">
    <div v-if="loading || !module" class="d-flex justify-content-center p-5">
      <b-spinner variant="primary" style="width: 3rem; height: 3rem;" />
    </div>
    <div
      v-else
      class="animate first-section container-fluid pr-lg-5 py-3 order-lg-1"
    >
      <header v-if="lesson.module && lesson.module.name" class="d-flex mb-3">
        <button
          class="btn btn-back text-decoration-none p-0 m-0"
          @click="goBack"
        >
          <i class="icon-chevron-left mr-2" /> {{ lesson.module.name }}
        </button>
      </header>
      <div class="row" :class="{ 'justify-content-center': isZoomLink }">
        <div v-if="isZoomLink" class="col-lg-12">
          <meeting :lesson="lesson" />
        </div>
        <div v-else class="col-lg-8 order-0">
          <blocked-module v-if="isBlocked.status" :isBlocked="isBlocked" />
          <empty-content
            v-else-if="!lesson.link_media && !lesson.description"
          />
          <video-player
            v-else-if="lesson.link_media"
            ref="video-player"
            :lesson="lesson"
            @save-progress="saveProgress"
            @ended="$router.push(getRoute(true))"
          />
        </div>
        <div
          v-if="!isZoomLink"
          class="col-lg-4 d-none d-lg-block"
          :class="{ 'order-lg-2': !lesson.link_media && lesson.description }"
        >
          <b-tabs justified>
            <b-tab title="Anotações" active v-if="!isZoomLink">
              <tab-notations
                :lesson="lesson"
                class=""
                @play="play()"
                @pause="pause()"
              />
            </b-tab>
            <b-tab title="Conteúdo do módulo" class="lesson-list-title">
              <tab-lesson-line-list :items="items" :current="lesson" />
            </b-tab>
          </b-tabs>
        </div>
        <div
          class="col-lg-8 animate second-section"
          :class="{ 'order-lg-1': !lesson.link_media && lesson.description }"
        >
          <content-description
            :content="lesson"
            :finishedLesson="finishedLesson"
            :loadingFinish="loadingFinish"
            :nextRoute="getRoute(true)"
            :previousRoute="getRoute()"
            :hide-finish-button="isBlocked.status"
            @toggleFinished="toggleFinished"
          />
          <div class="d-lg-none">
            <b-tabs justified>
              <b-tab title="Anotações" active v-if="!isZoomLink">
                <tab-notations :lesson="lesson" class="" />
              </b-tab>
              <b-tab title="Conteúdo do módulo" class="lesson-list-title">
                <tab-lesson-line-list :items="items" :current="lesson" />
              </b-tab>
            </b-tabs>
          </div>
          <lesson-material v-if="!isBlocked.status" :lesson="lesson" />

          <div v-if="lesson.comments_enabled">
            <send-comment :lesson="$route.params.lesson" @success="reload" />

            <div class="d-flex align-items-center">
              <div class="flex-fill mt-5 mb-3">
                Comentários ({{ commentsList.length }})
              </div>
              <b-spinner v-if="silentLoading" variant="primary" small />
            </div>
            <action-dispatcher
              action="forum/getAll"
              :parameters="{ params }"
              ref="forumDispatcher"
              @success="forumSuccess"
            >
              <div
                v-for="comment in commentsList"
                :key="'c' + comment.id"
                class="mb-5"
                :class="{ 'divider-border-bottom': commentsList.length > 1 }"
              >
                <comment
                  class="my-3"
                  :comment="comment"
                  @success="reload"
                  @success-noreload="silentReload"
                />
              </div>
            </action-dispatcher>

            <div class="text-center py-4">
              <button
                v-if="commentsList.length > 0 && showLoadMore"
                class="btn btn-next px-5"
                @click="loadMore"
              >
                <loading :show="loadingMore">Carregar mais</loading>
              </button>
            </div>
          </div>
        </div>
        <div class="col-lg-4 d-none d-lg-block" v-if="isZoomLink">
          <b-tabs class="p-4">
            <b-tab title="Conteúdo do módulo" class="lesson-list-title" active>
              <tab-lesson-line-list :items="items" :current="lesson" />
            </b-tab>
          </b-tabs>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import Comment from "@/components/lesson/Comment";
import ContentDescription from "@/components/lesson/ContentDescription";
import EmptyContent from "@/components/lesson/EmptyContent";
import LessonMaterial from "@/components/lesson/LessonMaterial";
import SendComment from "@/components/lesson/SendComment";
import TabLessonLineList from "@/components/lesson/TabLessonLineList";
import TabNotations from "@/components/lesson/TabNotations";
import VideoPlayer from "@/components/lesson/VideoPlayer";
import { calculateTimeFromNow } from "@/functions/helpers";
import { mapState } from "vuex";
import BlockedModule from "../components/lesson/BlockedModule.vue";
const Meeting = () => import("@/components/lesson/Meeting.vue");

export default {
  components: {
    ContentDescription,
    TabLessonLineList,
    LessonMaterial,
    TabNotations,
    VideoPlayer,
    EmptyContent,
    BlockedModule,
    SendComment,
    Meeting,
    Comment
  },
  data() {
    return {
      module: undefined,
      moduleParent: undefined,
      loading: false,
      loadingFinish: false,
      silentLoading: false,
      tab: "chapters",
      params: {
        id: this.$route.params.lesson,
        limit: 10,
        offset: 0
      },
      loadingMore: false,
      showLoadMore: false,
      typing: false
    };
  },
  computed: {
    ...mapState("forum", ["comments"]),
    ...mapState("modules", ["modules"]),
    ...mapState("lessons", ["lessons"]),
    ...mapState("quiz", ["quizes"]),
    ...mapState("user", ["user"]),
    lesson() {
      return this.lessons?.find(
        lesson => lesson.id === this.$route.params.lesson
      );
    },
    isBlocked() {
      const status =
        this.moduleParent?.is_blocked.status ||
        this.module?.is_blocked.status ||
        this.lesson?.is_blocked;

      if (!status)
        return { status, days_left_for_release: 0, blocked_by: null };

      const blocked_by =
        this.moduleParent?.is_blocked.status ||
        (this.module?.is_blocked.status && !this.moduleParent)
          ? "module"
          : this.moduleParent &&
            !this.moduleParent?.is_blocked.status &&
            this.module?.is_blocked.status
          ? "submodule"
          : "lesson";

      let days_left_for_release =
        this.moduleParent?.is_blocked.days_left_to_release ||
        this.moduleParent?.is_blocked.days_left_for_release ||
        this.module?.is_blocked.days_left_to_release ||
        this.module?.is_blocked.days_left_for_release;

      if (!days_left_for_release && this.lesson.days_to_release) {
        const date_joined = this.user.my_courses.find(
          course => course.product_id == this.lesson.product_id
        ).date_joined;

        const time_joined = calculateTimeFromNow(date_joined);
        const diff = this.lesson.days_to_release - time_joined;

        days_left_for_release = diff < 1 ? Math.floor(diff) : Math.ceil(diff);
      }

      if (!days_left_for_release && this.lesson.release_at) {
        days_left_for_release = Math.ceil(
          calculateTimeFromNow(this.lesson.release_at)
        );
      }

      return { status, days_left_for_release, blocked_by };
    },
    items() {
      return [...this.lessons, ...this.quizes].sort((a, b) => a.sort - b.sort);
    },
    commentsList() {
      return this.comments.filter(
        c => !(c.status == "review" && c.user_id !== this.$user.user.id)
      );
    },
    isZoomLink() {
      const zoomUrlRegex = /https:\/\/[\w-]*\.?zoom.us\/(j|my)\/[\d\w?=-]+/g;
      return this.lesson?.link_media?.match(zoomUrlRegex);
    },
    lessonIndex() {
      return this.items.findIndex(i => i.id === this.$route.params.lesson);
    },
    nextLesson() {
      //if there's a next content in the current module/submodule
      if (this.items[this.lessonIndex + 1]) {
        return {
          ...this.items[this.lessonIndex + 1],
          module: {
            id: this.$route.params.id
          }
        };
      }

      // if the current lesson is inside a submodule
      if (this.moduleParent) {
        const sub_module_index = this.moduleParent.sub_modules.findIndex(
          submodule => submodule.id == this.lesson.module.id
        );

        //if there's a next submodule with content
        if (
          this.moduleParent.sub_modules[sub_module_index + 1] &&
          this.moduleParent.sub_modules[sub_module_index + 1].items.length
        ) {
          return {
            ...this.moduleParent.sub_modules[sub_module_index + 1].items[0],
            module: {
              id: this.moduleParent.sub_modules[sub_module_index + 1].id
            }
          };
        }
      }

      const module_index = this.moduleParent
        ? this.modules.findIndex(module =>
            module.sub_modules.find(
              sub_module => sub_module.id === this.lesson.module.id
            )
          )
        : this.modules.findIndex(module => module.id == this.lesson.module.id);

      //if there's a submodule with content in the current module
      if (
        !this.moduleParent &&
        this.modules[module_index].sub_modules.length &&
        this.modules[module_index].sub_modules[0].items.length
      ) {
        return {
          ...this.modules[module_index].sub_modules[0].items[0],
          module: {
            id: this.modules[module_index].sub_modules[0].id
          }
        };
      }

      // look for a next module with content
      let nextModule = undefined;
      let i = module_index + 1;
      while (this.modules[i]) {
        nextModule = this.modules[i];

        //if there's a next module with content
        if (nextModule && nextModule.items.length) {
          const moduleFirstContent = nextModule.items.find(
            item => item.type !== "submodule"
          );
          //if there's a lesson in the next module (outside a submodule)
          if (moduleFirstContent) {
            return {
              ...moduleFirstContent,
              module: {
                id: nextModule.id
              }
            };
          }

          // look for content inside every submodule of the module
          let submoduleFirstContent = undefined;
          let j = 0;
          while (nextModule.sub_modules[j] && !submoduleFirstContent) {
            submoduleFirstContent = nextModule.sub_modules[j].items.find(
              item => item.type !== "submodule"
            );
            j++;
          }

          //if there's a submodule with lesson in the next module
          if (submoduleFirstContent) {
            return {
              ...submoduleFirstContent,
              module: {
                id: nextModule.sub_modules[0].id
              }
            };
          }
        }

        i++;
      }

      return null;
    },
    previousLesson() {
      //if there's a previous content in the current module/submodule
      if (this.items[this.lessonIndex - 1]) {
        return {
          ...this.items[this.lessonIndex - 1],
          module: {
            id: this.$route.params.id
          }
        };
      }

      // if the current lesson is inside a submodule
      if (this.moduleParent) {
        const sub_module_index = this.moduleParent.sub_modules.findIndex(
          submodule => submodule.id == this.lesson.module.id
        );

        const previousSubmodule = this.moduleParent.sub_modules[
          sub_module_index - 1
        ];
        //if there's a previous submodule with content
        if (previousSubmodule && previousSubmodule.items.length) {
          return {
            ...previousSubmodule.items.at(-1),
            module: {
              id: previousSubmodule.id
            }
          };
        }

        const previousContent = this.moduleParent.items.findLast(
          item => item.type !== "submodule"
        );
        // if there's content outside the submodule, in the same module
        if (previousContent) {
          return {
            ...previousContent,
            module: {
              id: this.moduleParent.id
            }
          };
        }
      }

      const module_index = this.moduleParent
        ? this.modules.findIndex(module =>
            module.sub_modules.find(
              sub_module => sub_module.id === this.lesson.module.id
            )
          )
        : this.modules.findIndex(module => module.id == this.lesson.module.id);

      // look for a previous module with content
      let previousModule = undefined;
      let i = module_index - 1;
      while (this.modules[i]) {
        previousModule = this.modules[i];

        // look for content inside every submodule of the previous module
        let submoduleLastContent = undefined;
        let j = previousModule.sub_modules.length - 1;
        while (previousModule.sub_modules[j] && !submoduleLastContent) {
          submoduleLastContent = previousModule.sub_modules[j].items.at(-1);

          //if there's a submodule with content in the previous module
          if (submoduleLastContent) {
            return {
              ...submoduleLastContent,
              module: {
                id: previousModule.sub_modules[0].id
              }
            };
          }

          j--;
        }

        //if the previous module has content outside of submodules
        if (previousModule && previousModule.items.length) {
          const moduleLastContent = previousModule.items.findLast(
            item => item.type !== "submodule"
          );
          //if there's content in the previous module (outside a submodule)
          if (moduleLastContent) {
            return {
              ...moduleLastContent,
              module: {
                id: previousModule.id
              }
            };
          }
        }

        i--;
      }

      return null;
    },
    finishedLesson() {
      return !!this.lesson?.progress?.finished_at;
    }
  },
  mounted() {
    this.loading = true;
    Promise.all([
      this.$store.dispatch("lessons/getAll", this.$route.params.id),
      this.$store.dispatch("quiz/getAll", {
        module_id: this.$route.params.id,
        params: { limit: 1000, status: "published" }
      })
    ])
      .then(() => {
        this.module = this.modules.find(m => m.id == this.lesson.module.id);

        if (!this.module) {
          this.moduleParent = this.modules.find(m =>
            m.sub_modules.some(s => s.id === this.lesson.module.id)
          );
          this.module = this.moduleParent.sub_modules.find(
            s => s.id === this.lesson.module.id
          );
          this.isSubmodule = true;
        }
      })
      .finally(() => {
        this.loading = false;
      });

    this.resetComments();
  },
  methods: {
    play() {
      if (this.typing) {
        setTimeout(() => {
          this.$refs["video-player"].player.play();
        }, 200);
        this.typing = false;
      }
    },
    pause() {
      if (!this.typing) this.$refs["video-player"].player.pause();
      this.typing = true;
    },
    goBack() {
      if (window.clientZoom) {
        window.clientZoom.leaveMeeting();
      }

      this.$router.push({
        name: "module",
        params: { id: this.$route.params.id }
      });
    },
    getRoute(next) {
      const route = next ? this.nextLesson : this.previousLesson;
      if (!route) return {};

      if (this.itemIsQuiz(route)) {
        return {
          name: "lesson-quiz",
          params: {
            id: route.module.id,
            quiz: route.code
          }
        };
      }
      return {
        name: "lesson",
        params: {
          id: route.module.id,
          lesson: route.id
        }
      };
    },
    itemIsQuiz(item) {
      return (
        item.type == "quiz" ||
        ["approval_average", "time_limit_minutes", "number_of_attempts"].reduce(
          (acc, cur) => acc && Object.keys(item).includes(cur),
          true
        )
      );
    },
    resetComments() {
      this.showLoadMore = true;
      this.params.offset = 0;
      this.$store.commit("forum/setComments", []);
    },
    reload() {
      this.resetComments();
      this.$refs.forumDispatcher.dispatch();
    },
    silentReload() {
      this.silentLoading = true;
      this.params.offset = 0;
      this.$store
        .dispatch("forum/getAll", {
          params: this.params,
          replaceComments: true
        })
        .finally(() => {
          this.silentLoading = false;
        });
    },
    forumSuccess(data) {
      this.showLoadMore = !!data?.next;
    },
    loadMore() {
      this.loadingMore = true;
      this.params.offset += this.params.limit;
      this.$store
        .dispatch("forum/getAll", { params: this.params })
        .then(data => {
          this.showLoadMore = !!data?.next;
        })
        .finally(() => {
          this.loadingMore = false;
        });
    },
    clickChapter(chapter) {
      const timeArr = chapter.time.split(":").map(str => parseInt(str));
      this.$refs["video-player"].player.currentTime =
        timeArr[0] * 60 * 60 + timeArr[1] * 60 + timeArr[2];
    },
    toggleFinished() {
      this.saveProgress({ finished: !this.finishedLesson, duration: 0 }, true);
    },
    saveProgress({ finished, duration }, refresh = false) {
      if (refresh) {
        this.loadingFinish = true;
      }
      this.$store
        .dispatch("modules/setProgress", {
          lesson_id: this.lesson.id,
          stopped_at: parseInt(duration),
          finished_at: finished
        })
        .then(() => {
          if (refresh) {
            setTimeout(() => {
              this.$store
                .dispatch("lessons/getAll", this.$route.params.id)
                .finally(() => {
                  this.loadingFinish = false;
                });
            }, 1000);
          }
        });
    }
  }
};
</script>

<style lang="scss" scoped>
.view-lesson {
  padding-top: 72px;
  min-height: calc(100vh - 56px);

  @media screen and (max-width: 991px) {
    flex-direction: column-reverse;
  }

  iframe {
    height: 500px;
  }
  .tabs {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
  ::v-deep .tab-content,
  .tab-pane {
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .animate {
    animation: fadeInAnimation ease 1s;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
    &.first-section {
      animation-delay: 0s;
      opacity: 0;
    }
    &.second-section {
      animation-delay: 1s;
      opacity: 0;
    }
  }

  @keyframes fadeInAnimation {
    0% {
      opacity: 0;
    }
    100% {
      opacity: 1;
    }
  }
}
</style>
